type ValidationIssue = { message: string; path: (string | number)[] };
type ValidationResult<T> = { success: true; data: T } | { success: false; error: ThemeParseError };

export class ThemeParseError extends Error {
  readonly issues: ValidationIssue[];
  constructor(message: string, path: (string | number)[] = []) {
    super(message);
    this.name = 'ThemeParseError';
    this.issues = [{ message, path }];
  }
}

function validationError(message: string, path: (string | number)[] = []): ThemeParseError {
  return new ThemeParseError(message, path);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isHexColour(value: unknown): value is string {
  return typeof value === 'string' && /^#[\da-fA-F]{6}$/.test(value);
}

function requireRecord(value: unknown, label: string): Record<string, unknown> {
  if (!isRecord(value)) throw validationError(`${label} must be an object.`);
  return value;
}

function requireHex(value: unknown, label: string): string {
  if (!isHexColour(value)) throw validationError(`${label} must be a six-digit hex colour.`);
  return value;
}

function requireNumber(value: unknown, label: string, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw validationError(`${label} must be a number between ${min} and ${max}.`);
  }
  return value;
}

function requireEnum<T extends string>(value: unknown, label: string, choices: readonly T[]): T {
  if (typeof value !== 'string' || !choices.includes(value as T)) {
    throw validationError(`${label} must be one of: ${choices.join(', ')}.`);
  }
  return value as T;
}

function requireString(value: unknown, label: string, min: number, max = Infinity): string {
  if (typeof value !== 'string' || value.length < min || value.length > max) {
    throw validationError(`${label} must be a string between ${min} and ${max} characters.`);
  }
  return value;
}

function rejectUnknownKeys(value: Record<string, unknown>, allowed: readonly string[], label: string): void {
  const unknown = Object.keys(value).find((key) => !allowed.includes(key));
  if (unknown) throw validationError(`Unrecognized key "${unknown}" in ${label}.`, [unknown]);
}

function parseThemeValue(value: unknown): Theme {
  const theme = requireRecord(value, 'Theme');
  rejectUnknownKeys(theme, ['id', 'name', 'scheme', 'colour', 'font', 'shape', 'density', 'kiosk', 'esp32', 'tokens'], 'Theme');
  const kiosk = requireRecord(theme.kiosk, 'kiosk');
  const esp32 = requireRecord(theme.esp32, 'esp32');
  const palette = requireRecord(esp32.palette, 'esp32.palette');
  const tokens = requireRecord(theme.tokens, 'tokens');
  rejectUnknownKeys(kiosk, ['scale'], 'kiosk');
  rejectUnknownKeys(esp32, ['palette'], 'esp32');
  rejectUnknownKeys(palette, ['bg', 'surface', 'ink', 'accent', 'ok', 'warn', 'danger'], 'esp32.palette');
  rejectUnknownKeys(tokens, ['bg', 'surface', 'sunken', 'ink', 'inkMuted', 'line', 'accent', 'accentInk', 'ok', 'warn', 'danger', 'info', 'radius', 'border'], 'tokens');

  const parsedPalette = {
    bg: requireHex(palette.bg, 'esp32.palette.bg'),
    surface: requireHex(palette.surface, 'esp32.palette.surface'),
    ink: requireHex(palette.ink, 'esp32.palette.ink'),
    accent: requireHex(palette.accent, 'esp32.palette.accent'),
    ok: requireHex(palette.ok, 'esp32.palette.ok'),
    warn: requireHex(palette.warn, 'esp32.palette.warn'),
    danger: requireHex(palette.danger, 'esp32.palette.danger')
  };
  const parsedTokens = {
    bg: requireHex(tokens.bg, 'tokens.bg'),
    surface: requireHex(tokens.surface, 'tokens.surface'),
    sunken: requireHex(tokens.sunken, 'tokens.sunken'),
    ink: requireHex(tokens.ink, 'tokens.ink'),
    inkMuted: requireHex(tokens.inkMuted, 'tokens.inkMuted'),
    line: requireHex(tokens.line, 'tokens.line'),
    accent: requireHex(tokens.accent, 'tokens.accent'),
    accentInk: requireHex(tokens.accentInk, 'tokens.accentInk'),
    ok: requireHex(tokens.ok, 'tokens.ok'),
    warn: requireHex(tokens.warn, 'tokens.warn'),
    danger: requireHex(tokens.danger, 'tokens.danger'),
    info: requireHex(tokens.info, 'tokens.info'),
    radius: requireNumber(tokens.radius, 'tokens.radius', 0, 8),
    border: requireNumber(tokens.border, 'tokens.border', 1, 4)
  };
  const parsed: Theme = {
    id: requireString(theme.id, 'id', 1),
    name: requireString(theme.name, 'name', 1),
    scheme: requireEnum(theme.scheme, 'scheme', ['light', 'dark']),
    colour: requireEnum(theme.colour, 'colour', ['signal', 'console']),
    font: requireEnum(theme.font, 'font', ['system']),
    shape: requireEnum(theme.shape, 'shape', ['square']),
    density: requireEnum(theme.density, 'density', ['compact', 'comfortable']),
    kiosk: { scale: requireNumber(kiosk.scale, 'kiosk.scale', 1, 2) },
    esp32: { palette: parsedPalette },
    tokens: parsedTokens
  };

  const textPairs: Array<[string, string, string]> = [
    ['ink', parsed.tokens.ink, parsed.tokens.bg],
    ['ink', parsed.tokens.ink, parsed.tokens.surface],
    ['inkMuted', parsed.tokens.inkMuted, parsed.tokens.bg],
    ['inkMuted', parsed.tokens.inkMuted, parsed.tokens.surface],
    ['accentInk', parsed.tokens.accentInk, parsed.tokens.accent],
    ['ESP32 ink', parsed.esp32.palette.ink, parsed.esp32.palette.bg]
  ];
  for (const [name, foreground, background] of textPairs) {
    if (contrastRatio(foreground, background) < 4.5) {
      throw validationError(`${name} contrast must be at least 4.5:1`, ['tokens', name]);
    }
  }
  return parsed;
}

function parseTenantBranding(value: unknown): TenantBranding {
  const brand = requireRecord(value, 'Tenant branding');
  rejectUnknownKeys(brand, ['accent', 'accentInk', 'logo', 'name', 'radius'], 'Tenant branding');
  const parsed: TenantBranding = {
    accent: requireHex(brand.accent, 'accent'),
    accentInk: requireHex(brand.accentInk, 'accentInk')
  };
  if (brand.logo !== undefined) {
    if (typeof brand.logo !== 'string' || !URL.canParse(brand.logo)) {
      throw validationError('logo must be a valid URL.', ['logo']);
    }
    parsed.logo = brand.logo;
  }
  if (brand.name !== undefined) parsed.name = requireString(brand.name, 'name', 1, 80);
  if (brand.radius !== undefined) parsed.radius = requireNumber(brand.radius, 'radius', 0, 8);
  if (contrastRatio(parsed.accentInk, parsed.accent) < 4.5) {
    throw validationError('Brand accent text contrast must be at least 4.5:1', ['accentInk']);
  }
  return parsed;
}

function schema<T>(parseValue: (value: unknown) => T) {
  return {
    parse: parseValue,
    safeParse(value: unknown): ValidationResult<T> {
      try {
        return { success: true, data: parseValue(value) };
      } catch (cause) {
        if (cause instanceof ThemeParseError) return { success: false, error: cause };
        const message = cause instanceof Error ? cause.message : String(cause);
        return { success: false, error: new ThemeParseError(message) };
      }
    }
  };
}

function luminance(hex: string): number {
  const channels = hex.slice(1).match(/.{2}/g)?.map((channel) => {
    const value = parseInt(channel, 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  }) ?? [0, 0, 0];
  return (channels[0] ?? 0) * 0.2126 + (channels[1] ?? 0) * 0.7152 + (channels[2] ?? 0) * 0.0722;
}

export function contrastRatio(first: string, second: string): number {
  const values = [luminance(first), luminance(second)].sort((a, b) => b - a);
  const lighter = values[0] ?? 0;
  const darker = values[1] ?? 0;
  return (lighter + 0.05) / (darker + 0.05);
}

export type Theme = {
  id: string;
  name: string;
  scheme: 'light' | 'dark';
  colour: 'signal' | 'console';
  font: 'system';
  shape: 'square';
  density: 'compact' | 'comfortable';
  kiosk: { scale: number };
  esp32: { palette: { bg: string; surface: string; ink: string; accent: string; ok: string; warn: string; danger: string } };
  tokens: { bg: string; surface: string; sunken: string; ink: string; inkMuted: string; line: string; accent: string; accentInk: string; ok: string; warn: string; danger: string; info: string; radius: number; border: number };
};

export type TenantBranding = {
  accent: string;
  accentInk: string;
  logo?: string;
  name?: string;
  radius?: number;
};

export const ThemeSchema = schema(parseThemeValue);
export const TenantBrandingSchema = schema(parseTenantBranding);

export function resolveAccentInk(accent: string): string {
  if (!isHexColour(accent)) {
    throw new Error('Accent must be a six-digit hex colour.');
  }
  const dark = '#101714';
  const light = '#ffffff';
  return contrastRatio(dark, accent) >= contrastRatio(light, accent) ? dark : light;
}

/**
 * `#rrggbb` → the space-separated RGB channels a CSS custom property needs
 * (`rgb(var(--accent) / 0.5)` composes from them). One implementation, shared by
 * the app theme loader and the player's frozen tenant branding (f10 P3.4).
 */
export function hexToChannels(hex: string): string {
  if (!isHexColour(hex)) {
    throw new Error('Expected a six-digit hex colour.');
  }
  return (hex.slice(1).match(/.{2}/g) ?? []).map((part) => parseInt(part, 16)).join(' ');
}

export function parseTheme(value: unknown): Theme {
  return ThemeSchema.parse(value);
}